/**
 * Phase 5.9 — Activity Feed + Online Users Panel.
 *
 * Embedded in DispatchDashboard as a new tab. Two columns:
 *   LEFT  — live activity feed of recent customer_events
 *   RIGHT — list of currently-online users (last_seen <2 min)
 *
 * Real-time updates: subscribes to the SSE bus (presence.online / .offline /
 * customer_event.logged) and auto-refetches on each event. Falls back to a
 * 30-second poll if SSE is disconnected.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useSSE } from '../hooks/useSSE';import { OnlineDot } from './OnlineDot';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Users, Activity, RefreshCcw, Filter as FilterIcon, BarChart3, ChevronDown, ChevronUp } from 'lucide-react';
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import CustomerActivityPanel from './CustomerActivityPanel';
import { ActivityPeopleList } from './ActivityPeopleList';
import { EVENT_STYLE, styleForEvent, fmtRelative, summarize, roleColor } from './activityFeedShared';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// Phase 6.0 P1 — format a Date for an <input type="datetime-local"> value
// (which expects local-tz "YYYY-MM-DDTHH:mm", NOT ISO with offset).
function toLocalDatetimeInput(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ActivityFeedPanel({ isSuperAdmin, selectedRegion }) {
  const [feed, setFeed] = useState([]);
  // Feb 2026 — customer slide-over panel state (Option B). Clicking a customer
  // name in the feed opens the panel WITHOUT navigating away from the
  // dispatcher's active tab.
  const [activePanelUserId, setActivePanelUserId] = useState(null);
  const [online, setOnline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sinceMinutes, setSinceMinutes] = useState(60);
  // Phase 6.0 P1 — custom date range mode. When the window selector is set to
  // 'custom', the user can pick a start + end datetime (datetime-local) and we
  // send start_ts/end_ts query params instead of since_minutes.
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  // Sparkline collapse state — collapsed by default so the feed isn't pushed
  // down on the initial dispatcher load.
  const [showSparkline, setShowSparkline] = useState(false);
  const [view, setView] = useState('people');
  const [refreshKey, setRefreshKey] = useState(0);
  const [peopleStats, setPeopleStats] = useState(null);

  const isCustomRange = sinceMinutes === 'custom';

  const regionParam = useMemo(() => {
    if (!isSuperAdmin) return '';
    return selectedRegion === 'all' ? 'all' : selectedRegion;
  }, [isSuperAdmin, selectedRegion]);

  const windowInfo = useMemo(() => {
    const qs = new URLSearchParams();
    if (eventTypeFilter !== 'all') qs.set('event_type', eventTypeFilter);
    if (roleFilter !== 'all') qs.set('role', roleFilter);
    let minutes = 60;
    if (isCustomRange) {
      if (customStart) qs.set('start_ts', new Date(customStart).toISOString());
      if (customEnd) qs.set('end_ts', new Date(customEnd).toISOString());
      if (customStart) {
        minutes = Math.max(1, Math.round(((customEnd ? new Date(customEnd) : Date.now()) - new Date(customStart)) / 60000));
      }
    } else if (sinceMinutes) {
      qs.set('since_minutes', String(sinceMinutes));
      minutes = Number(sinceMinutes) || 60;
    }
    if (regionParam) qs.set('region', regionParam);
    return { qs: qs.toString(), minutes };
  }, [eventTypeFilter, roleFilter, sinceMinutes, regionParam, isCustomRange, customStart, customEnd]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setRefreshKey((k) => k + 1);
    try {
      // Live list stays capped; the People view covers the whole window server-side.
      const limit = windowInfo.minutes <= 60 ? 60 : windowInfo.minutes <= 1440 ? 200 : 500;
      const [feedRes, onlineRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/activity-feed?${windowInfo.qs}&limit=${limit}`, { withCredentials: true }),
        axios.get(
          `${API_URL}/api/presence/online?limit=200${regionParam ? `&region=${regionParam}` : ''}`,
          { withCredentials: true },
        ),
      ]);
      setFeed(feedRes.data.events || []);
      setOnline(onlineRes.data.online || []);
    } catch (_e) {
      // Silent — presence/activity is supplementary info.
    } finally {
      setLoading(false);
    }
  }, [windowInfo, regionParam]);

  // Initial + filter-change refetch
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // SSE-driven live refresh on new events (debounced so a burst of events
  // doesn't fire N refetches in a single tick).
  const fetchAllRef = useRef(fetchAll);
  fetchAllRef.current = fetchAll;
  const sseDebounceRef = useRef(null);
  useSSE(useCallback((evt) => {
    if (!evt || !evt.type) return;
    if (evt.type !== 'presence.online' && evt.type !== 'presence.offline'
        && evt.type !== 'customer_event.logged') return;
    if (sseDebounceRef.current) return;
    sseDebounceRef.current = setTimeout(() => {
      sseDebounceRef.current = null;
      fetchAllRef.current?.();
    }, 800);
  }, []));

  // Fallback poll every 30s in case SSE drops
  useEffect(() => {
    const t = setInterval(fetchAll, 30_000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const onlineCounts = useMemo(() => {
    const acc = { all: online.length, customer: 0, driver: 0, dispatcher: 0, super_admin: 0 };
    for (const o of online) {
      if (acc[o.role] !== undefined) acc[o.role] += 1;
    }
    return acc;
  }, [online]);

  // Phase 6.0 P1 — bucket the feed into time buckets for the sparkline.
  // Bucket size auto-adjusts with the window: 1m for 15m, 5m for 1h, 15m for 6h,
  // 1h for 24h, 6h for 7d. Always returns exactly 24 buckets for visual rhythm.
  const sparklineData = useMemo(() => {
    if (feed.length === 0) return [];
    // Resolve the [start, end] window we're bucketing over.
    let start;
    let end;
    if (isCustomRange) {
      start = customStart ? new Date(customStart) : null;
      end = customEnd ? new Date(customEnd) : new Date();
      if (!start) {
        // No explicit start — derive from oldest event in feed (gives a sane chart).
        const oldest = feed[feed.length - 1]?.ts;
        if (!oldest) return [];
        let s = String(oldest);
        if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
        start = new Date(s);
      }
    } else {
      end = new Date();
      start = new Date(end.getTime() - (Number(sinceMinutes) || 60) * 60_000);
    }
    const windowMs = end - start;
    if (windowMs <= 0) return [];
    const bucketCount = 24;
    const bucketMs = windowMs / bucketCount;
    const buckets = Array.from({ length: bucketCount }, (_, i) => ({
      t: start.getTime() + i * bucketMs,
      count: 0,
    }));
    for (const ev of feed) {
      let s = String(ev.ts || '');
      if (!s) continue;
      if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
      const ts = new Date(s).getTime();
      if (!Number.isFinite(ts) || ts < start.getTime() || ts > end.getTime()) continue;
      const idx = Math.min(bucketCount - 1, Math.floor((ts - start.getTime()) / bucketMs));
      buckets[idx].count += 1;
    }
    // Format short tick labels for the X axis (every 6th bucket gets a label
    // to keep the chart legible).
    return buckets.map((b, i) => ({
      label: i % 6 === 0
        ? new Date(b.t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '',
      tooltip: new Date(b.t).toLocaleString(),
      count: b.count,
    }));
  }, [feed, isCustomRange, customStart, customEnd, sinceMinutes]);

  const totalEventsInWindow = sparklineData.reduce((acc, b) => acc + b.count, 0);

  // Region pill label — mirrors the top region toggle so dispatchers see at a
  // glance which slice of the feed they're looking at.
  const regionPillLabel = !isSuperAdmin
    ? (selectedRegion || '').toUpperCase()
    : (selectedRegion === 'all' || !selectedRegion ? 'ALL REGIONS' : selectedRegion.toUpperCase());

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="activity-feed-panel">
      {/* LEFT — Activity feed */}
      <div className="lg:col-span-2 bg-black/40 border border-[var(--terminal-border)] rounded-md p-3">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-[#33FF57]" />
          <h3 className="text-sm font-mono text-[#33FF57]">// ACTIVITY FEED</h3>
          {/* Phase 6.0 P1 — region pill mirrors the top region toggle so the
              feed's current scope is visible at a glance. */}
          {regionPillLabel && (
            <Badge
              variant="outline"
              className="text-[10px] font-mono px-1.5 py-0 border-[#7FD8FF]/40 text-[#7FD8FF]"
              data-testid="activity-feed-region-pill"
              title={isSuperAdmin ? 'Region scope (mirrors top toggle)' : 'Your region (locked)'}
            >
              {regionPillLabel}
            </Badge>
          )}
          <Badge variant="outline" className="ml-auto text-xs" data-testid="activity-feed-count">
            {view === 'people'
              ? `${peopleStats?.count ?? 0} people · ${peopleStats?.events ?? 0} events`
              : `${feed.length} ${feed.length === 1 ? 'event' : 'events'}`}
          </Badge>
          {/* Phase 6.0 P1 — sparkline toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSparkline((v) => !v)}
            className="h-8 px-2"
            data-testid="activity-sparkline-toggle"
            title={showSparkline ? 'Hide volume chart' : 'Show volume chart'}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            {showSparkline ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
          </Button>
          <Button
            data-testid="activity-feed-refresh"
            variant="outline"
            size="sm"
            onClick={fetchAll}
            disabled={loading}
            className="h-8 px-2"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Phase 6.0 P1 — collapsible event-volume sparkline. Buckets the
            current feed into 24 time slices over the active window. */}
        {showSparkline && (
          <div
            className="mb-3 px-2 py-2 border border-[var(--terminal-border)] rounded bg-black/30"
            data-testid="activity-sparkline"
          >
            <div className="flex items-center justify-between mb-1">
              <span
                className="text-[10px] font-mono text-[#888] cursor-help"
                title="Sum of events matching your current filter (event type · audience · region · time window), bucketed across 24 equal time slices. The numbers in the cards above use the SAME filter — when both show ≠0, they agree. cart.abandon events are written by a 5-minute server sweep, so newly-idle carts appear in this chart within ~5 min."
                data-testid="event-volume-label"
              >
                // event volume · {totalEventsInWindow} total
                <span className="text-[#33FF57]"> (?)</span>
              </span>
              <span className="text-[10px] font-mono text-[#33FF57]">24 buckets</span>
            </div>
            <div className="h-24">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sparklineData} margin={{ top: 4, right: 6, left: 0, bottom: 0 }}>
                  <XAxis
                    dataKey="label"
                    stroke="#666"
                    tick={{ fill: '#888', fontSize: 9, fontFamily: 'monospace' }}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(51, 255, 87, 0.08)' }}
                    contentStyle={{
                      background: '#0a0a0a',
                      border: '1px solid #33FF57',
                      borderRadius: 4,
                      fontFamily: 'monospace',
                      fontSize: 11,
                      color: '#33FF57',
                    }}
                    labelStyle={{ color: '#7FD8FF' }}
                    labelFormatter={(_lbl, payload) => payload?.[0]?.payload?.tooltip || ''}
                    formatter={(v) => [`${v} ${v === 1 ? 'event' : 'events'}`, '']}
                  />
                  <Bar dataKey="count" fill="#33FF57" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
          <FilterIcon className="w-3.5 h-3.5 text-[#888]" />
          <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
            <SelectTrigger className="h-8 w-[170px] text-xs" data-testid="activity-event-type-filter">
              <SelectValue placeholder="Event type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All event types</SelectItem>
              {Object.keys(EVENT_STYLE).sort().map((k) => (
                <SelectItem key={k} value={k}>{k}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="activity-role-filter">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="customer">Customers</SelectItem>
              <SelectItem value="driver">Drivers</SelectItem>
              <SelectItem value="dispatcher">Dispatchers</SelectItem>
              <SelectItem value="super_admin">Super-admin</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={String(sinceMinutes)}
            onValueChange={(v) => {
              if (v === 'custom') {
                setSinceMinutes('custom');
                // Seed the custom range with the last 24h on first switch so the
                // operator isn't staring at an empty chart.
                if (!customStart) {
                  const yesterday = new Date(Date.now() - 24 * 60 * 60_000);
                  setCustomStart(toLocalDatetimeInput(yesterday));
                }
                if (!customEnd) setCustomEnd(toLocalDatetimeInput(new Date()));
              } else {
                setSinceMinutes(parseInt(v, 10) || 60);
              }
            }}
          >
            <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="activity-window-filter">
              <SelectValue placeholder="Window" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="15">Last 15m</SelectItem>
              <SelectItem value="60">Last 1h</SelectItem>
              <SelectItem value="360">Last 6h</SelectItem>
              <SelectItem value="1440">Last 24h</SelectItem>
              <SelectItem value="10080">Last 7d</SelectItem>
              <SelectItem value="custom">Custom range…</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Phase 6.0 P1 — custom date range row (only visible in custom mode) */}
        {isCustomRange && (
          <div
            className="flex flex-wrap items-center gap-2 mb-3 text-xs font-mono"
            data-testid="activity-custom-range-row"
          >
            <span className="text-[#7FD8FF]">// range:</span>
            <Input
              type="datetime-local"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="h-8 w-[180px] text-xs bg-black/40"
              data-testid="activity-custom-start"
            />
            <span className="text-[#888]">→</span>
            <Input
              type="datetime-local"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="h-8 w-[180px] text-xs bg-black/40"
              data-testid="activity-custom-end"
            />
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2 text-[10px]"
              onClick={() => {
                setCustomStart('');
                setCustomEnd('');
                setSinceMinutes(60);
              }}
              data-testid="activity-custom-clear"
            >
              CLEAR
            </Button>
          </div>
        )}

        {/* View tabs */}
        <div className="flex gap-1 mb-3 text-[11px] font-mono">
          {[['people', 'People'], ['live', 'Live events']].map(([k, lbl]) => (
            <button key={k} type="button" onClick={() => setView(k)} data-testid={`activity-view-${k}`}
              className={`px-3 py-1.5 uppercase tracking-wider border rounded-sm transition-colors ${view === k ? 'border-[#33FF57] text-[#33FF57]' : 'border-[var(--terminal-border)] text-[#888] hover:text-[#A3C7A8]'}`}>
              {lbl}
            </button>
          ))}
        </div>

        {view === 'people' ? (
          <ActivityPeopleList qs={windowInfo.qs} refreshKey={refreshKey} onOpenProfile={setActivePanelUserId} onLoaded={setPeopleStats} />
        ) : (
        <div className="max-h-[600px] overflow-y-auto pr-1 space-y-1" data-testid="activity-feed-list">
          {feed.length === 0 ? (
            <div className="text-center text-[#888] text-sm py-8">
              {loading ? 'Loading…' : '// no events in window'}
            </div>
          ) : feed.map((ev) => {
            const s = styleForEvent(ev.event_type);
            return (
              <div
                key={ev.id}
                data-testid={`activity-event-${ev.id}`}
                className="flex items-center gap-2 px-2 py-1.5 rounded border border-transparent hover:border-[var(--terminal-border)] hover:bg-black/40 transition-colors text-xs font-mono"
              >
                <span
                  className="inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-bold whitespace-nowrap"
                  style={{ background: `${s.color}22`, color: s.color, border: `1px solid ${s.color}55` }}
                >
                  {s.label}
                </span>
                <span className="text-[var(--terminal-text)] truncate min-w-0 flex-1">
                  {ev.customer_id ? (
                    // Feb 2026 — clicking a name opens the slide-over Customer
                    // Activity Panel instead of navigating to /dispatch/admin/users.
                    // The full-profile deep-link is still available from inside
                    // the panel's "FULL PROFILE" button.
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActivePanelUserId(ev.customer_id);
                      }}
                      className="hover:underline cursor-pointer bg-transparent border-0 p-0 font-inherit"
                      style={{ color: roleColor(ev.actor_role) }}
                      data-testid={`activity-actor-link-${ev.customer_id}`}
                    >
                      {ev.actor_name || ev.actor_email || ev.customer_id?.slice(0, 8)}
                    </button>
                  ) : (
                    <span style={{ color: roleColor(ev.actor_role) }}>
                      {ev.actor_name || ev.actor_email || ev.customer_id?.slice(0, 8)}
                    </span>
                  )}
                  <span className="text-[#888] ml-1">
                    {summarize(ev.payload, ev.event_type) ? `· ${summarize(ev.payload, ev.event_type)}` : ''}
                  </span>
                </span>
                {ev.region && (
                  <Badge variant="outline" className="text-[10px] px-1 py-0">{ev.region}</Badge>
                )}
                <span className="text-[#888] text-[10px] whitespace-nowrap">{fmtRelative(ev.ts)}</span>
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* RIGHT — Online now */}
      <div className="bg-black/40 border border-[var(--terminal-border)] rounded-md p-3">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-[#33FF57]" />
          <h3 className="text-sm font-mono text-[#33FF57]">// ONLINE NOW</h3>
          <Badge variant="outline" className="ml-auto text-xs" data-testid="online-count-total">
            {onlineCounts.all}
          </Badge>
        </div>

        {/* Role count strip */}
        <div className="grid grid-cols-3 gap-1 mb-3 text-[10px] font-mono">
          <div className="px-2 py-1 rounded bg-black/40 border border-[var(--terminal-border)] text-center">
            <div className="text-[#33FF57] text-sm" data-testid="online-count-customer">{onlineCounts.customer}</div>
            <div className="text-[#888]">cust</div>
          </div>
          <div className="px-2 py-1 rounded bg-black/40 border border-[var(--terminal-border)] text-center">
            <div className="text-[#FF8C42] text-sm" data-testid="online-count-driver">{onlineCounts.driver}</div>
            <div className="text-[#888]">drv</div>
          </div>
          <div className="px-2 py-1 rounded bg-black/40 border border-[var(--terminal-border)] text-center">
            <div className="text-[#7FD8FF] text-sm" data-testid="online-count-dispatcher">
              {onlineCounts.dispatcher + onlineCounts.super_admin}
            </div>
            <div className="text-[#888]">disp</div>
          </div>
        </div>

        <div className="max-h-[560px] overflow-y-auto pr-1 space-y-1" data-testid="online-users-list">
          {online.length === 0 ? (
            <div className="text-center text-[#888] text-sm py-8">// no one online</div>
          ) : online.map((u) => (
            <div
              key={u.user_id}
              data-testid={`online-user-${u.user_id}`}
              className="flex items-center gap-2 px-2 py-1.5 rounded border border-transparent hover:border-[var(--terminal-border)] hover:bg-black/40 text-xs font-mono"
            >
              <OnlineDot online size={8} />
              <div className="min-w-0 flex-1 truncate">
                <Link
                  to={`/dispatch/admin/users?openUser=${u.user_id}&from=activity-feed`}
                  className="hover:underline cursor-pointer"
                  style={{ color: roleColor(u.role) }}
                  data-testid={`online-user-link-${u.user_id}`}
                >
                  {u.name || u.email || u.user_id.slice(0, 8)}
                </Link>
                <span className="text-[#888] ml-1">{u.role}</span>
              </div>
              {u.region && (
                <Badge variant="outline" className="text-[10px] px-1 py-0">{u.region}</Badge>
              )}
              <span className="text-[#888] text-[10px] whitespace-nowrap">{fmtRelative(u.last_seen)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Feb 2026 — Option B slide-over panel. Renders as a portal-like fixed
          overlay when a customer name is clicked in the feed. */}
      {activePanelUserId && (
        <CustomerActivityPanel
          customerId={activePanelUserId}
          onClose={() => setActivePanelUserId(null)}
        />
      )}
    </div>
  );
}
